/**
 * Dựng một tệp `.docx` tối giản, KHÔNG dùng thư viện ngoài.
 *
 * ── VÌ SAO KHÔNG THÊM `docx` VÀO PACKAGE.JSON ──────────────────────────────
 * Kho cấm thêm thư viện cho một tệp hướng dẫn; lockfile điều khiển mọi Docker build qua `--frozen-lockfile`.
 *
 * `.docx` là một tệp ZIP chứa vài XML. Ghi ZIP ở chế độ STORE (không nén) thì
 * không cần thư viện nén — chỉ cần CRC32 và đúng thứ tự byte của định dạng.
 * Word mở tệp STORE bình thường.
 *
 * Phạm vi CỐ Ý hẹp: tiêu đề, đoạn văn, gạch đầu dòng, chữ đậm, khối mã (mẫu
 * tệp trả lời phải in đúng từng dấu cách). Đủ cho một tài liệu hướng dẫn. Cần bảng, ảnh hay kiểu dáng thật thì lúc ấy hãy lấy thư viện
 * — đừng nới dần chỗ này thành một bộ dựng Word nửa vời.
 */

export type Khoi =
  | { loai: 'tieuDe'; chu: string; cap?: 1 | 2 }
  | { loai: 'doan'; chu: string }
  | { loai: 'gach'; chu: string }
  /** Mỗi dòng một đoạn chữ đơn cách, giữ nguyên khoảng trắng, KHÔNG đọc `**đậm**`. */
  | { loai: 'ma'; chu: string };

// ── CRC32, bảng dựng một lần ──────────────────────────────────────────────
const BANG_CRC = (() => {
  const b = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    b[i] = c >>> 0;
  }
  return b;
})();

function crc32(u8: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < u8.length; i += 1) c = BANG_CRC[(c ^ u8[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** XML escape. Thiếu nó thì một dấu `&` trong hướng dẫn làm hỏng cả tệp. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * `**đậm**` → `<w:r><w:rPr><w:b/></w:rPr>…`. Cùng cú pháp mà lời văn của trợ lí
 * đang dùng, nên người viết hướng dẫn không phải học một quy ước thứ hai.
 */
function chay(chu: string): string {
  return chu
    .split(/\*\*(.+?)\*\*/g)
    .map((phan, i) =>
      phan
        ? `<w:r>${i % 2 ? '<w:rPr><w:b/></w:rPr>' : ''}` +
          `<w:t xml:space="preserve">${esc(phan)}</w:t></w:r>`
        : '',
    )
    .join('');
}

function maXml(chu: string): string {
  return chu
    .split('\n')
    .map(
      (dong) =>
        '<w:p><w:pPr><w:pStyle w:val="Code"/></w:pPr><w:r>' +
        `<w:t xml:space="preserve">${esc(dong)}</w:t></w:r></w:p>`,
    )
    .join('');
}

function doanXml(k: Khoi): string {
  if (k.loai === 'ma') return maXml(k.chu);
  if (k.loai === 'tieuDe') {
    return (
      `<w:p><w:pPr><w:pStyle w:val="Heading${k.cap ?? 1}"/></w:pPr>${chay(k.chu)}</w:p>`
    );
  }
  if (k.loai === 'gach') {
    return `<w:p><w:pPr><w:pStyle w:val="ListParagraph"/><w:numPr><w:ilvl w:val="0"/><w:numId w:val="1"/></w:numPr></w:pPr>${chay(k.chu)}</w:p>`;
  }
  return `<w:p>${chay(k.chu)}</w:p>`;
}

const CONTENT_TYPES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/><Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/><Override PartName="/word/numbering.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.numbering+xml"/></Types>`;

const RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>`;

const DOC_RELS = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/numbering" Target="numbering.xml"/></Relationships>`;

const STYLES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:style w:type="paragraph" w:styleId="Heading1"><w:name w:val="heading 1"/><w:pPr><w:spacing w:before="280" w:after="120"/><w:outlineLvl w:val="0"/></w:pPr><w:rPr><w:b/><w:sz w:val="32"/></w:rPr></w:style><w:style w:type="paragraph" w:styleId="Heading2"><w:name w:val="heading 2"/><w:pPr><w:spacing w:before="220" w:after="100"/><w:outlineLvl w:val="1"/></w:pPr><w:rPr><w:b/><w:sz w:val="26"/></w:rPr></w:style><w:style w:type="paragraph" w:styleId="Code"><w:name w:val="Code"/><w:pPr><w:spacing w:before="0" w:after="0"/><w:ind w:left="360"/></w:pPr><w:rPr><w:rFonts w:ascii="Consolas" w:hAnsi="Consolas" w:cs="Consolas"/><w:sz w:val="18"/></w:rPr></w:style><w:style w:type="paragraph" w:styleId="ListParagraph"><w:name w:val="List Paragraph"/><w:pPr><w:ind w:left="720"/></w:pPr></w:style></w:styles>`;

const NUMBERING = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:numbering xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:abstractNum w:abstractNumId="0"><w:lvl w:ilvl="0"><w:start w:val="1"/><w:numFmt w:val="bullet"/><w:lvlText w:val="•"/><w:lvlJc w:val="left"/><w:pPr><w:ind w:left="720" w:hanging="360"/></w:pPr></w:lvl></w:abstractNum><w:num w:numId="1"><w:abstractNumId w:val="0"/></w:num></w:numbering>`;

function documentXml(khoi: Khoi[]): string {
  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body>` +
    khoi.map(doanXml).join('') +
    `<w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1134" w:right="1134" w:bottom="1134" w:left="1134"/></w:sectPr></w:body></w:document>`
  );
}

// ── ZIP (STORE) ───────────────────────────────────────────────────────────

interface Muc {
  ten: string;
  data: Uint8Array;
}

function u16(n: number): number[] {
  return [n & 0xff, (n >>> 8) & 0xff];
}
function u32(n: number): number[] {
  return [n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff];
}

function zip(mucs: Muc[]): Blob {
  const enc = new TextEncoder();
  const cuc: number[] = [];
  const trungTam: number[] = [];
  let offset = 0;

  for (const m of mucs) {
    const ten = enc.encode(m.ten);
    const crc = crc32(m.data);
    // Ngày giờ DOS đặt cứng: một tệp hướng dẫn sinh ra hai lần trong cùng một
    // phút mà khác byte thì không so được. Cố định luôn cho dễ đối chiếu.
    const gio = 0;
    const ngay = 0x21; // 1980-01-01

    const dauCuc = [
      ...u32(0x04034b50), ...u16(20), ...u16(0), ...u16(0), // STORE
      ...u16(gio), ...u16(ngay), ...u32(crc),
      ...u32(m.data.length), ...u32(m.data.length),
      ...u16(ten.length), ...u16(0),
    ];
    cuc.push(...dauCuc, ...ten, ...m.data);

    trungTam.push(
      ...u32(0x02014b50), ...u16(20), ...u16(20), ...u16(0), ...u16(0),
      ...u16(gio), ...u16(ngay), ...u32(crc),
      ...u32(m.data.length), ...u32(m.data.length),
      ...u16(ten.length), ...u16(0), ...u16(0), ...u16(0), ...u16(0),
      ...u32(0), ...u32(offset), ...ten,
    );
    offset += dauCuc.length + ten.length + m.data.length;
  }

  const cuoi = [
    ...u32(0x06054b50), ...u16(0), ...u16(0),
    ...u16(mucs.length), ...u16(mucs.length),
    ...u32(trungTam.length), ...u32(offset), ...u16(0),
  ];
  return new Blob([new Uint8Array([...cuc, ...trungTam, ...cuoi])], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
}

/** Các khối nội dung → một Blob `.docx` mở được bằng Word. */
export function taoDocx(khoi: Khoi[]): Blob {
  const enc = new TextEncoder();
  return zip([
    // `[Content_Types].xml` PHẢI là mục đầu tiên — Word đọc tuần tự và bỏ tệp
    // nếu không thấy nó ngay.
    { ten: '[Content_Types].xml', data: enc.encode(CONTENT_TYPES) },
    { ten: '_rels/.rels', data: enc.encode(RELS) },
    { ten: 'word/_rels/document.xml.rels', data: enc.encode(DOC_RELS) },
    { ten: 'word/document.xml', data: enc.encode(documentXml(khoi)) },
    { ten: 'word/styles.xml', data: enc.encode(STYLES) },
    { ten: 'word/numbering.xml', data: enc.encode(NUMBERING) },
  ]);
}

/**
 * Đẩy Blob xuống máy người dùng. Tệp này nằm ở `nghiep-vu/` (chạy được dưới `node --test`, không có DOM) nên đọc
 * `document` qua kiểu tối thiểu thay vì lib DOM; màn hình mới có thể dùng `luuBlob` của `lib/api/http` — cùng việc.
 */
interface PhanTuA {
  href: string;
  download: string;
  rel: string;
  click(): void;
  remove(): void;
}
interface TaiLieuToiThieu {
  createElement(ten: 'a'): PhanTuA;
  body: { append(a: PhanTuA): void };
}
export function taiVe(blob: Blob, ten: string): void {
  const doc = (globalThis as unknown as { document?: TaiLieuToiThieu }).document;
  if (!doc) return;
  const url = URL.createObjectURL(blob);
  const a = doc.createElement('a');
  a.href = url;
  a.download = ten;
  a.rel = 'noopener';
  doc.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
